import axios from 'axios';
import { project } from '../hooks/auth';
import { isViewSessionActive, notifyViewSessionExpired } from './publicViewSession';

// Configuração da URL base da API
const getBaseURL = () => {
    // Verifica se estamos em desenvolvimento e se a variável de ambiente está definida
    return process.env.REACT_APP_API_LOCAL_URL;
};

const Api = axios.create({
    baseURL: getBaseURL()
});

// Função para processar notifications do backend
const processNotifications = (notifications) => {
    if (notifications && Array.isArray(notifications) && notifications.length > 0) {
        // Importar Toast dinamicamente para evitar circular imports
        import('../components/ui/toast').then((toastModule) => {
            const Toast = toastModule.default;
            notifications.forEach((notif, index) => {
                if (notif.message) {
                    setTimeout(() => {
                        Toast.error(notif.message, {
                            id: `backend-notification-${notif.identifier || Date.now()}-${index}`,
                            duration: 6000,
                        });
                    }, index * 200);
                }
            });
        }).catch(console.error);
        return true;
    }
    return false;
};

// Chamadas que exibem a própria mensagem de erro podem pedir para o interceptor
// não abrir o toast automático das notifications:
// Api.post(url, data, { skipNotificationToast: true })
const shouldSkipNotificationToast = (config) => config?.skipNotificationToast === true;

// Interceptor para responses (respostas)
Api.interceptors.response.use(
    (response) => {
        // Response de sucesso - verificar se há notifications de sucesso
        if (response.data?.notifications && !shouldSkipNotificationToast(response.config)) {
            processNotifications(response.data.notifications);
        }
        return response;
    },
    (error) => {
        console.error('Erro da API:', error);
        
        // Verificar se há notifications no response do erro
        if (error.response?.data?.notifications) {
            if (shouldSkipNotificationToast(error.config)) {
                // Quem fez a chamada exibe a mensagem por conta própria
                return Promise.reject(error);
            }

            const hasNotifications = processNotifications(error.response.data.notifications);
            if (hasNotifications) {
                // Rejeitar o erro para que os componentes possam capturar
                return Promise.reject(error);
            }
        }
        
        // Importar Toast dinamicamente para fallback
        import('../components/ui/toast').then((toastModule) => {
            const Toast = toastModule.default;
            
            // Fallback para tratamento de erros sem notifications
            if (error.response?.status === 500) {
                Toast.error('Erro interno do servidor.');
            } else if (error.response?.status === 404) {
                Toast.error('Rota não encontrada.');
            } else if (error.response?.status === 401) {
                // Visitante anônimo na tela de visualização não tem para onde ser deslogado:
                // limpar o localStorage e mandar para /signIn seria um beco sem saída. O correto
                // é sinalizar a expiração para o /view reabrir o modal de PIN.
                if (isViewSessionActive()) {
                    notifyViewSessionExpired();
                } else {
                    sessionStorage.clear();
                    localStorage.removeItem(`${project}:token`);
                    localStorage.removeItem(`${project}:user`);
                    localStorage.removeItem(`${project}:tenant`);
                    localStorage.removeItem(`${project}:authData`);
                    localStorage.removeItem(`${project}:userName`);
                    localStorage.removeItem(`${project}:modules`);
                    window.location.href = window.location.origin + '/signIn';
                }
            } else if (error.response?.status === 400) {
                // Para erros 400 sem notifications específicas
                Toast.error(error.response?.data?.message || 'Dados inválidos.');
            } else if (!error.response) {
                // Erro de rede
                Toast.error('Não foi possível conectar ao servidor.');
            }
        }).catch(console.error);
        
        return Promise.reject(error);
    }
);

export const useExceptionNotification = () => {

    const exceptionNotificationAPI = (error) => {
        // Função legacy mantida para compatibilidade
        // Agora usa Toast ao invés de notification
        import('../components/ui/toast').then((toastModule) => {
            const Toast = toastModule.default;
            
            if (/500/.test(error.message)) {
                Toast.error('Erro interno do servidor (500).');
            } else if (/404/.test(error.message)) {
                Toast.error('Rota não encontrada (404).');
            } else if (/401/.test(error.message)) {
                sessionStorage.clear();
                localStorage.removeItem(`${project}:token`);
                localStorage.removeItem(`${project}:user`);
                localStorage.removeItem(`${project}:tenant`);
                localStorage.removeItem(`${project}:authData`);
                localStorage.removeItem(`${project}:userName`);
                localStorage.removeItem(`${project}:modules`);
                window.location.href = window.location.origin + '/signIn';
            } else if (/400/.test(error.message)) {
                let notifications = error.response?.data?.notifications;
                if (notifications && notifications.length > 0) {
                    Toast.processBackendNotifications(error);
                } else {
                    Toast.error('Erro de validação.');
                }
            } else {
                Toast.error(error.message || 'Erro desconhecido.');
            }
        }).catch(console.error);
    }

    return exceptionNotificationAPI;
}

export default Api;
